import { useState } from "react";
import { useQuery, type QueryClient } from "@tanstack/react-query";
import {
  buyersApi,
  buyerSubmissionsApi,
  listingsApi,
  type BuyerLeadSummaryDto,
  type BuyerSubmissionPresentationDto,
  type ListingListParams,
  type ListingSummaryDto,
} from "@/api/buyers";
import {
  Button,
  EmptyState,
  Input,
  Skeleton,
  StatusBadge,
  type StatusTone,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
// SHORT throughout. Every name in this file lands in a picker row, a selected
// card or a suggested-match row — one-line slots stacked in a scrolling list,
// where a marketplace pitch pushes the location/price caption that distinguishes
// two similar listings out of view.
import {
  listingShortName,
  listingShortNameOrFallback,
  shortenListingName,
} from "@/lib/listingDisplay";
import { buyerKeys, buyerSubmissionKeys, listingKeys } from "@/hooks/buyers/buyerKeys";
import { InlineQueryError } from "../queryStates";
import {
  listingMatchApi,
  type ListingMatchDto,
  type ListingMatchesDto,
} from "./listingMatchApi";
import { listingMatchKeys } from "./listingMatchKeys";
import {
  alreadyPresentedReason,
  LISTING_NO_CRITERIA_BLURB,
  LISTING_NO_PRICE_BLURB,
  LISTING_NOT_CONSIDERED_BLURB,
  LISTING_TIER_SEARCH_BLURB,
  LISTING_TIER_SHORTLIST_BLURB,
  LISTING_TIER_SUGGESTED_BLURB,
} from "./presentBuyerCopy";

/**
 * The two search-and-choose halves of the Present-a-buyer wizard.
 *
 * <p>The buyer half is one column and stays one — there is nothing to rank a
 * buyer against. The listing half is three tiers, because there was a real
 * defect in making it one: a search box over a SHARED catalog, with the buyer
 * standing right there and nothing joining the two. A specialist who knew the
 * budget to the dollar still had to type a business name they were trying to be
 * reminded of, and the two things they almost always want — what this buyer is
 * already looking at, and what in the catalog fits them — were both a search
 * away and neither was offered.</p>
 */

/**
 * What the wizard needs of a chosen listing.
 *
 * <p>Deliberately narrower than `ListingSummaryDto`: the three tiers hand back
 * three different row shapes (a summary, a match's summary, and an association
 * row that carries no `createdAt` for the LISTING and no submission count), and
 * widening them all to a full summary would mean minting fields nobody has —
 * a `submissionCount: 0` on a listing with three submissions is a lie a caller
 * would have no way to detect.</p>
 */
export interface PickedListing {
  id: string;
  displayName?: string;
  businessName?: string | null;
  askingPrice?: number | null;
  /** The listing's stated cash flow. `undefined` means the tier's row does not
   *  carry it (the shortlist row) — not that the listing states none; read it
   *  with {@link resolvePickedListingCashFlow}. */
  cashFlow?: number | null;
  city?: string | null;
  state?: string | null;
}

/**
 * The picked listing's stated cash flow, fetching the listing when the row it
 * was picked from does not carry one (the shortlist's association row has no
 * cash-flow column). Read through the listing's own detail key, so a second
 * pick of the same listing costs nothing. Null when the listing states none or
 * the read fails — the field it prefills stays typeable either way.
 */
export async function resolvePickedListingCashFlow(
  qc: QueryClient,
  listing: PickedListing,
): Promise<number | null> {
  if (listing.cashFlow !== undefined) return listing.cashFlow ?? null;
  try {
    const detail = await qc.fetchQuery({
      queryKey: listingKeys.detail(listing.id),
      queryFn: () => listingsApi.get(listing.id),
    });
    return detail.cashFlow ?? null;
  } catch {
    return null;
  }
}

/** The buyer criteria the search tier seeds itself from. */
export interface ListingPickerCriteria {
  maxPurchasePrice?: number | null;
  targetCashFlowMin?: number | null;
}

// ---------------------------------------------------------------------------

/**
 * The standing presentations (ruling R1) for one side of the pairing, keyed by
 * the OTHER side's id — what a picker disables, and the reason it gives.
 *
 * <p>🔴 R1: "where a picker lists buyers or listings, the already-presented ones
 * show disabled with the same reason, rather than failing after selection". A
 * failed read disables nothing: the wizard's own check and create's 409 still
 * refuse, so the cost is a later refusal, never a second submission.</p>
 */
function usePresentations(
  params: { buyerLeadId?: string; listingId?: string },
  key: "listingId" | "buyerLeadId",
  enabled: boolean,
): Map<string, BuyerSubmissionPresentationDto> {
  const q = useQuery({
    queryKey: buyerSubmissionKeys.presentations(params),
    queryFn: () => buyerSubmissionsApi.presentations(params),
    enabled,
  });
  return new Map((q.data ?? []).map((p) => [p[key], p]));
}

export function BuyerPicker({
  selected,
  onSelect,
  onClear,
  enabled,
  listingId,
}: {
  selected: BuyerLeadSummaryDto | null;
  onSelect: (b: BuyerLeadSummaryDto) => void;
  onClear: () => void;
  enabled: boolean;
  /** The listing already settled, when there is one — its presented buyers are
   *  shown but cannot be chosen (R1). */
  listingId?: string | null;
}) {
  const [search, setSearch] = useState("");
  const params = { page: 1, pageSize: 10, q: search.trim() || undefined };

  const results = useQuery({
    queryKey: buyerKeys.list(params),
    queryFn: () => buyersApi.list(params),
    enabled,
  });

  const presented = usePresentations(
    { listingId: listingId ?? undefined },
    "buyerLeadId",
    enabled && Boolean(listingId),
  );

  return (
    <PickerColumn
      label="Buyer"
      testId="submission-buyer"
      search={search}
      onSearch={setSearch}
      placeholder="Search buyers by name, company or email"
      loading={results.isLoading}
      failed={results.isError}
      onRetry={() => void results.refetch()}
      retrying={results.isFetching}
      failureNoun="the buyers list"
      rows={results.data?.data ?? []}
      selected={selected}
      onSelect={onSelect}
      onClear={onClear}
      selectedLabel={(b) => b.displayName}
      selectedCaption={(b) =>
        [b.email, b.assignedSpecialistName ?? "Unassigned"].filter(Boolean).join(" · ")
      }
      /* A row leads with the company for a company buyer, like the buyers
         list (ruling Q5); the chosen chip above keeps the person. */
      rowLabel={(b) => b.staffDisplayName ?? b.displayName}
      rowCaption={(b) =>
        [b.email, b.city && b.state ? `${b.city}, ${b.state}` : b.state]
          .filter(Boolean)
          .join(" · ")
      }
      /* 🔴 Read the server's own answer rather than re-deriving the ownership
         rule here. A buyer you do not own would 403 on create, and a picker
         that offers a guaranteed refusal is worse than one that explains. */
      rowDisabled={(b) => !b.canEdit || presented.has(b.id)}
      /* Presented first when both apply: reassigning the buyer would still
         leave the pair refused. */
      rowDisabledReason={(b) => {
        const p = presented.get(b.id);
        if (p) return alreadyPresentedReason(p);
        return b.assignedSpecialistName
          ? `Assigned to ${b.assignedSpecialistName}`
          : "Unassigned — ask a Buyer Manager";
      }}
    />
  );
}

// ---------------------------------------------------------------------------

/** Which tier is open. Only one at a time — three lists of listings stacked
 *  open is a wall, not a picker. */
export type Tier = "shortlist" | "suggested" | "search";

const ALL_TIERS: readonly Tier[] = ["shortlist", "suggested", "search"];

/** Catalog rows per page. Small on purpose: this is a picker inside a modal
 *  step, and the pager below it is what reaches the rest. */
const PAGE_SIZE = 10;

export function ListingPicker({
  selected,
  onSelect,
  onClear,
  onCreateNew,
  enabled,
  buyerLeadId,
  buyerName,
  criteria,
  allowPresented = false,
  tiers,
}: {
  selected: PickedListing | null;
  onSelect: (l: PickedListing) => void;
  /** Drop the current choice and come back to the tiers. */
  onClear: () => void;
  /**
   * 🔴 The dead end this closes. "No listings match — try a different search"
   * was the whole answer, on the one surface where the specialist most likely
   * has the listing in front of them and it simply is not in the catalog yet.
   * Leaving to create it loses everything typed here, so the way out is
   * offered in place. Omitted where the host has its own way out (the call
   * types the deal in instead), and the action is then not offered.
   */
  onCreateNew?: () => void;
  enabled: boolean;
  /** Absent until the buyer half is settled — the shortlist and the ranking are
   *  both facts about a PAIRING, so neither can be asked before then. */
  buyerLeadId?: string | null;
  buyerName?: string | null;
  /** 🔴 The plumbing that existed and had never been used: `ListingListQuery`
   *  has carried `askingPriceMax` and `cashFlowMin` since the catalog shipped
   *  and nothing in the app had ever sent them. */
  criteria?: ListingPickerCriteria | null;
  /** 🔴 Lift R1's bar. Presenting the buyer is what R1 refuses twice; a host
   *  that only NAMES a listing (the call's prequalification, which records the
   *  listing on the attempt and creates no submission) offers every listing,
   *  presented or not. Off by default, so the wizard is unchanged. */
  allowPresented?: boolean;
  /** The tiers to offer, in order; the first one available opens. Omitted, the
   *  wizard's three (Suggested first when there is a buyer). */
  tiers?: readonly Tier[];
}) {
  const offered = (t: Tier) =>
    (tiers ?? ALL_TIERS).includes(t) && (t === "search" || Boolean(buyerLeadId));
  const [search, setSearch] = useState("");
  // Seeded ON from the buyer, and REVERSIBLE. A budget filter applied silently
  // is the same defect as the exact-match industry filter it sits beside: a
  // specialist searching for one particular listing that happens to be over
  // budget would get an empty list reading as "there are none".
  const [useCriteria, setUseCriteria] = useState(true);
  const [page, setPage] = useState(1);
  const [tier, setTier] = useState<Tier>(() =>
    tiers
      ? (tiers.find((t) => t === "search" || Boolean(buyerLeadId)) ?? "search")
      : buyerLeadId
        ? "suggested"
        : "search",
  );

  const hasCriteria = Boolean(
    criteria && (criteria.maxPurchasePrice != null || criteria.targetCashFlowMin != null),
  );
  const criteriaOn = hasCriteria && useCriteria;

  const params: ListingListParams = {
    page,
    pageSize: PAGE_SIZE,
    q: search.trim() || undefined,
    askingPriceMax: criteriaOn ? criteria?.maxPurchasePrice ?? undefined : undefined,
    cashFlowMin: criteriaOn ? criteria?.targetCashFlowMin ?? undefined : undefined,
  };

  const results = useQuery({
    queryKey: listingKeys.list(params),
    queryFn: () => listingsApi.list(params),
    enabled: enabled && tier === "search",
  });

  const shortlist = useQuery({
    queryKey: buyerKeys.associatedListings(buyerLeadId ?? ""),
    queryFn: () => buyersApi.listAssociatedListings(buyerLeadId!),
    enabled: enabled && Boolean(buyerLeadId),
  });

  const suggestions = useQuery({
    queryKey: listingMatchKeys.forBuyer(buyerLeadId ?? ""),
    queryFn: () => listingMatchApi.forBuyer(buyerLeadId!),
    enabled: enabled && Boolean(buyerLeadId) && tier === "suggested",
  });

  // Every tier disables a listing this buyer is already presented to (R1) —
  // unless the host lifts the bar, when there is nothing to read.
  const standing = usePresentations(
    { buyerLeadId: buyerLeadId ?? undefined },
    "listingId",
    enabled && Boolean(buyerLeadId) && !allowPresented,
  );
  const presented = allowPresented
    ? new Map<string, BuyerSubmissionPresentationDto>()
    : standing;

  if (selected) {
    return (
      <div className="space-y-space-2">
        <PickerLabel>Listing</PickerLabel>
        <SelectedCard
          testId="submission-listing"
          label={listingShortNameOrFallback(selected.displayName, selected.businessName)}
          caption={locationAndPrice(selected)}
          changeLabel="Choose a different listing"
          /* 🔴 It CLEARS the choice. The shared column this replaced only reset
             its own search box, so "Choose a different listing" left the chosen
             card exactly where it was — a control that looked like the way back
             and did nothing. */
          onChange={() => {
            setSearch("");
            onClear();
          }}
        />
      </div>
    );
  }

  const shortlistCount = shortlist.data?.length ?? 0;

  return (
    <div className="space-y-space-2" data-testid="submission-listing-tiers">
      <PickerLabel>Listing</PickerLabel>

      {/* Toggle buttons rather than an ARIA tablist: a tablist owes its reader
          roving focus and a labelled tabpanel, and a half-built one is worse
          for a screen reader than plain buttons that say what they are. */}
      <div className="flex flex-wrap gap-space-1">
        {offered("shortlist") && (
          <TierTab
            id="shortlist"
            active={tier}
            onSelect={setTier}
            label={buyerName ? `On ${buyerName}'s list` : "On this buyer's list"}
            count={shortlist.isLoading ? undefined : shortlistCount}
          />
        )}
        {offered("suggested") && (
          <TierTab id="suggested" active={tier} onSelect={setTier} label="Suggested" />
        )}
        {offered("search") && (
          <TierTab id="search" active={tier} onSelect={setTier} label="Search the catalog" />
        )}
      </div>

      {tier === "shortlist" && (
        <TierBody blurb={LISTING_TIER_SHORTLIST_BLURB} testId="listing-tier-shortlist">
          {shortlist.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : shortlist.isError ? (
            <InlineQueryError
              what="this buyer's list"
              onRetry={() => void shortlist.refetch()}
              retrying={shortlist.isFetching}
            />
          ) : shortlistCount === 0 ? (
            <EmptyState
              title="Nothing on this buyer's list yet"
              description="Tie listings on their record, or find one below."
              variant="noResults"
              className="py-space-4"
            />
          ) : (
            <ul className="grid gap-space-1 list-none p-0 m-0 max-h-64 overflow-y-auto">
              {(shortlist.data ?? []).map((row) => {
                // The association row names the standing submission; only the
                // presentations read knows whether it is archived.
                const p = presented.get(row.listingId);
                const isPresented =
                  !allowPresented && (Boolean(row.submissionId) || Boolean(p));
                return (
                  <li key={row.id}>
                    <RowButton
                      testId="listing-shortlist-row"
                      label={listingShortNameOrFallback(
                        row.listingDisplayName,
                        row.listingBusinessName,
                      )}
                      caption={[
                        [row.listingCity, row.listingState].filter(Boolean).join(", "),
                        row.listingIndustry,
                        row.listingAskingPrice != null
                          ? formatCurrency(row.listingAskingPrice)
                          : "No price stated",
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                      /* 🔴 Already presented IS a bar (ruling R1): any non-deleted
                         submission for the pair, archived included, refuses a new
                         one. Shown disabled with the reason, rather than failing
                         two steps on. */
                      disabled={isPresented}
                      disabledReason={
                        isPresented
                          ? alreadyPresentedReason({
                              status: row.submissionStatus ?? p?.status,
                              isArchived: p?.isArchived,
                            })
                          : undefined
                      }
                      onClick={() =>
                        onSelect({
                          id: row.listingId,
                          displayName: row.listingDisplayName,
                          businessName: row.listingBusinessName,
                          askingPrice: row.listingAskingPrice,
                          city: row.listingCity,
                          state: row.listingState,
                        })
                      }
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </TierBody>
      )}

      {tier === "suggested" && (
        <TierBody blurb={LISTING_TIER_SUGGESTED_BLURB} testId="listing-tier-suggested">
          {suggestions.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : suggestions.isError ? (
            <InlineQueryError
              what="the suggestions"
              onRetry={() => void suggestions.refetch()}
              retrying={suggestions.isFetching}
            />
          ) : !suggestions.data?.buyerHasCriteria ? (
            <p
              className="text-small text-gray-mid m-0"
              data-testid="listing-suggested-no-criteria"
            >
              {LISTING_NO_CRITERIA_BLURB}
            </p>
          ) : (
            <SuggestedBody data={suggestions.data} onSelect={onSelect} presented={presented} />
          )}
        </TierBody>
      )}

      {tier === "search" && (
        <TierBody blurb={LISTING_TIER_SEARCH_BLURB} testId="listing-tier-search">
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              // A new term is a new result set — staying on page 3 of the last
              // one shows an empty page and reads as "no matches".
              setPage(1);
            }}
            placeholder="Search business, headline, industry, city or marketplace"
            aria-label="Search the listing catalog"
            data-testid="submission-listing-search"
          />

          {hasCriteria && (
            <label
              className="flex items-start gap-space-2 text-caption text-gray-mid"
              data-testid="submission-listing-criteria-filter"
            >
              <input
                type="checkbox"
                className="mt-[2px]"
                checked={useCriteria}
                onChange={(e) => {
                  setUseCriteria(e.target.checked);
                  setPage(1);
                }}
              />
              <span>
                Only what this buyer can buy — {criteriaSummary(criteria)}. Turn it off to
                search the whole catalog.
              </span>
            </label>
          )}

          {results.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : results.isError ? (
            <InlineQueryError
              what="the listings catalog"
              onRetry={() => void results.refetch()}
              retrying={results.isFetching}
            />
          ) : (results.data?.data.length ?? 0) === 0 ? (
            <EmptyState
              title="No listings match"
              description={
                criteriaOn
                  ? "It may not be in the catalog yet — or it may be outside this buyer's budget, which the filter above can be turned off."
                  : "It may not be in the catalog yet."
              }
              variant="noResults"
              className="py-space-4"
              action={
                onCreateNew
                  ? { label: "Add this listing to the catalog", onClick: onCreateNew }
                  : undefined
              }
            />
          ) : (
            <ul
              className="grid gap-space-1 list-none p-0 m-0 max-h-64 overflow-y-auto"
              data-testid="submission-listing-results"
            >
              {(results.data?.data ?? []).map((row) => {
                const p = presented.get(row.id);
                return (
                  <li key={row.id}>
                    <RowButton
                      testId="listing-search-row"
                      label={shortenListingName(row.displayName) ?? listingShortName(row)}
                      caption={locationAndPrice(row)}
                      disabled={Boolean(p)}
                      disabledReason={p ? alreadyPresentedReason(p) : undefined}
                      onClick={() => onSelect(row)}
                    />
                  </li>
                );
              })}
            </ul>
          )}

          {/* 🔴 SERVER-paged, not a fixed first page. The catalog is shared and
              unbounded, and a picker that silently showed the first ten of two
              hundred matches would look identical to one that had found ten. */}
          {!results.isLoading && !results.isError && (results.data?.totalCount ?? 0) > PAGE_SIZE && (
            <div
              className="flex items-center justify-between gap-space-2 text-caption text-gray-mid"
              data-testid="submission-listing-pager"
            >
              <span>
                {(page - 1) * PAGE_SIZE + 1}–
                {Math.min(page * PAGE_SIZE, results.data?.totalCount ?? 0)} of{" "}
                {results.data?.totalCount ?? 0}
              </span>
              <span className="flex items-center gap-space-1">
                <Button
                  variant="text"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="text"
                  size="sm"
                  disabled={page * PAGE_SIZE >= (results.data?.totalCount ?? 0)}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </span>
            </div>
          )}

          {/* Offered alongside real results too, not only when the search comes
              back empty: a near-miss match is the case where a specialist most
              needs to be able to say "no, mine is a different business". */}
          {onCreateNew && !results.isLoading && !results.isError && (results.data?.data.length ?? 0) > 0 && (
            <Button
              variant="text"
              size="sm"
              onClick={onCreateNew}
              data-testid="submission-listing-create-new"
            >
              Add this listing to the catalog
            </Button>
          )}
        </TierBody>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/** The Suggested tier's body — the ranked group, the unpriced group, and the
 *  criteria nothing could measure. */
function SuggestedBody({
  data,
  onSelect,
  presented,
}: {
  data: ListingMatchesDto;
  onSelect: (l: PickedListing) => void;
  /** The server already excludes presented listings from this tier; the map is
   *  the belt to that — a listing presented since the ranking was read. */
  presented: Map<string, BuyerSubmissionPresentationDto>;
}) {
  const [showUnpriced, setShowUnpriced] = useState(false);

  return (
    <div className="grid gap-space-3">
      {data.matches.length === 0 && data.noPriceStatedTotal === 0 ? (
        <EmptyState
          title="Nothing to suggest yet"
          description={
            data.alreadyPresentedExcluded > 0
              ? `Every listing in the catalog has already been presented to this buyer (${data.alreadyPresentedExcluded}). Their submissions are on the buyer's record.`
              : "The catalog has nothing that can be ranked against this buyer yet."
          }
          variant="noResults"
          className="py-space-4"
        />
      ) : (
        <ul className="grid gap-space-2 list-none p-0 m-0 max-h-80 overflow-y-auto">
          {data.matches.map((m) => (
            <li key={m.listing.id}>
              <MatchRow match={m} onSelect={onSelect} presented={presented.get(m.listing.id)} />
            </li>
          ))}
        </ul>
      )}

      {/* 🔴 Its own group, never a silent exclusion. */}
      {data.noPriceStatedTotal > 0 && (
        <div className="grid gap-space-2" data-testid="listing-suggested-no-price">
          <button
            type="button"
            className="text-left text-caption uppercase tracking-wide text-gold-shadow hover:underline"
            onClick={() => setShowUnpriced((s) => !s)}
            aria-expanded={showUnpriced}
          >
            {showUnpriced ? "Hide" : "Show"} {data.noPriceStatedTotal} with no price stated
          </button>
          {showUnpriced && (
            <>
              <p className="text-caption text-gray-mid m-0">{LISTING_NO_PRICE_BLURB}</p>
              <ul className="grid gap-space-2 list-none p-0 m-0 max-h-64 overflow-y-auto">
                {data.noPriceStated.map((m) => (
                  <li key={m.listing.id}>
                    <MatchRow
                      match={m}
                      onSelect={onSelect}
                      presented={presented.get(m.listing.id)}
                    />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {data.candidatesTruncated && (
        <p className="text-caption text-gray-mid m-0" data-testid="listing-suggested-truncated">
          Ranked over the {data.candidatesConsidered} most recently added listings, not the
          whole catalog. Search below for anything older.
        </p>
      )}

      {/* 🔴 Named, with NO verdict. Showing these as misses would claim the
          catalog judged them; showing nothing would let a specialist believe a
          rank accounted for a buyer's credit band. */}
      {data.notConsidered.length > 0 && (
        <details className="text-caption text-gray-mid" data-testid="listing-not-considered">
          <summary className="cursor-pointer text-gold-shadow">
            What the ranking does not account for ({data.notConsidered.length})
          </summary>
          <p className="m-0 mt-space-1">{LISTING_NOT_CONSIDERED_BLURB}</p>
          <ul className="grid gap-space-1 list-none p-0 m-0 mt-space-1">
            {data.notConsidered.map((c) => (
              <li key={c.criterion}>
                <span className="text-onyx">{c.criterion}</span> — {c.why}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/** One suggestion: the row, the score, and why. */
function MatchRow({
  match,
  onSelect,
  presented,
}: {
  match: ListingMatchDto;
  onSelect: (l: PickedListing) => void;
  presented?: BuyerSubmissionPresentationDto;
}) {
  const [showAll, setShowAll] = useState(false);

  // Scored dimensions first, heaviest first — the reasons that produced the
  // number lead, and the blanks are behind the toggle rather than absent.
  const scored = match.reasons.filter((r) => r.verdict !== "unknown");
  const unknown = match.reasons.filter((r) => r.verdict === "unknown");
  const ordered = [...scored].sort((a, b) => b.weight - a.weight);
  const shown = showAll ? [...ordered, ...unknown] : ordered.slice(0, 3);
  const hidden = ordered.length + unknown.length - shown.length;

  return (
    <div className="rounded-sm border border-border-light p-space-2">
      <button
        type="button"
        onClick={() => onSelect(match.listing)}
        disabled={Boolean(presented)}
        className="w-full text-left disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="listing-suggested-row"
      >
        <span className="flex items-start justify-between gap-space-2">
          <span className="min-w-0">
            <span className="block text-small text-onyx">
              {listingShortNameOrFallback(match.listing.displayName, match.listing.businessName)}
            </span>
            <span className="block text-caption text-gray-mid">
              {presented ? alreadyPresentedReason(presented) : locationAndPrice(match.listing)}
            </span>
          </span>
          <span className="shrink-0 text-right">
            <StatusBadge tone={scoreTone(match.score)}>
              {match.score == null ? "Not scored" : `${match.score}`}
            </StatusBadge>
            <span className="block text-caption text-gray-mid mt-[2px]">
              scored on {match.scoredDimensions} of {match.totalDimensions}
            </span>
          </span>
        </span>
      </button>

      <ul className="grid gap-[2px] list-none p-0 m-0 mt-space-1">
        {shown.map((r) => (
          <li key={r.dimension} className="flex items-start gap-space-1 text-caption">
            <span className={`shrink-0 ${verdictClass(r.verdict)}`}>{verdictMark(r.verdict)}</span>
            <span className="text-gray-mid">
              <span className="text-onyx">{r.dimension}:</span> {r.detail}
            </span>
          </li>
        ))}
      </ul>

      {hidden > 0 && !showAll && (
        <button
          type="button"
          className="mt-space-1 text-caption text-gold-shadow hover:underline"
          onClick={() => setShowAll(true)}
        >
          {hidden} more, including what could not be measured
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small shared pieces
// ---------------------------------------------------------------------------

function PickerLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="block text-caption uppercase tracking-wide text-gray-mid">
      {children}
    </span>
  );
}

function TierTab({
  id,
  active,
  onSelect,
  label,
  count,
}: {
  id: Tier;
  active: Tier;
  onSelect: (t: Tier) => void;
  label: string;
  count?: number;
}) {
  const isActive = active === id;
  return (
    <button
      type="button"
      onClick={() => onSelect(id)}
      aria-pressed={isActive}
      data-testid={`listing-tier-tab-${id}`}
      className={`rounded-pill border px-space-2 py-[2px] text-caption ${
        isActive
          ? "border-gold bg-gold/15 text-onyx"
          : "border-border-light bg-white-warm text-gray-mid hover:border-gold"
      }`}
    >
      {label}
      {count != null && <span className="ml-space-1 text-gray-mid">{count}</span>}
    </button>
  );
}

function TierBody({
  blurb,
  testId,
  children,
}: {
  blurb: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-space-2" data-testid={testId}>
      <p className="text-caption text-gray-mid m-0">{blurb}</p>
      {children}
    </div>
  );
}

function RowButton({
  testId,
  label,
  caption,
  onClick,
  disabled,
  disabledReason,
}: {
  testId: string;
  label: string;
  caption: string;
  onClick: () => void;
  disabled?: boolean;
  /** Replaces the caption while disabled — the PickerColumn idiom, so a greyed
   *  row always says why. */
  disabledReason?: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
      className="w-full text-left rounded-sm border border-border-light px-space-2 py-space-1 hover:border-gold disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:border-border-light"
    >
      <span className="flex items-start justify-between gap-space-2">
        <span className="min-w-0">
          <span className="block text-small text-onyx">{label}</span>
          <span className="block text-caption text-gray-mid">
            {disabled && disabledReason ? disabledReason : caption}
          </span>
        </span>
      </span>
    </button>
  );
}

function SelectedCard({
  testId,
  label,
  caption,
  changeLabel,
  onChange,
}: {
  testId: string;
  label: string;
  caption: string;
  changeLabel: string;
  onChange: () => void;
}) {
  return (
    <div
      className="rounded-md border border-border-light bg-white-warm p-space-3"
      data-testid={`${testId}-selected`}
    >
      <div className="text-small text-onyx">{label}</div>
      <div className="text-caption text-gray-mid">{caption}</div>
      <button
        type="button"
        className="text-caption text-gold-shadow hover:underline mt-space-1"
        data-testid={`${testId}-change`}
        onClick={onChange}
      >
        {changeLabel}
      </button>
    </div>
  );
}

function locationAndPrice(l: PickedListing | ListingSummaryDto): string {
  return [
    [l.city, l.state].filter(Boolean).join(", "),
    l.askingPrice != null ? formatCurrency(l.askingPrice) : "No price stated",
  ]
    .filter(Boolean)
    .join(" · ");
}

function criteriaSummary(criteria?: ListingPickerCriteria | null): string {
  const parts: string[] = [];
  if (criteria?.maxPurchasePrice != null) {
    parts.push(`up to ${formatCurrency(criteria.maxPurchasePrice)}`);
  }
  if (criteria?.targetCashFlowMin != null) {
    parts.push(`cash flow ${formatCurrency(criteria.targetCashFlowMin)}+`);
  }
  return parts.join(", ");
}

function scoreTone(score: number | null): StatusTone {
  if (score == null) return "muted";
  if (score >= 75) return "ok";
  if (score >= 45) return "warn";
  return "neutral";
}

function verdictMark(verdict: string): string {
  switch (verdict) {
    case "hit":
      return "✓";
    case "near":
      return "~";
    case "miss":
      return "✕";
    default:
      return "–";
  }
}

function verdictClass(verdict: string): string {
  switch (verdict) {
    case "hit":
      return "text-status-success";
    case "near":
      return "text-status-warning-ink";
    case "miss":
      return "text-status-error";
    default:
      return "text-gray-mid";
  }
}

// ---------------------------------------------------------------------------

/** One search-and-choose column — the buyer half. */
function PickerColumn<T extends { id: string }>({
  label,
  testId,
  search,
  onSearch,
  placeholder,
  loading,
  failed,
  onRetry,
  retrying,
  failureNoun,
  rows,
  selected,
  onSelect,
  selectedLabel,
  selectedCaption,
  rowLabel,
  rowCaption,
  rowDisabled,
  rowDisabledReason,
  onClear,
}: {
  label: string;
  testId: string;
  search: string;
  onSearch: (v: string) => void;
  placeholder: string;
  loading: boolean;
  /** A read that failed is NOT an empty catalog — the two mean opposite things,
   *  and "no listings match" would send the specialist off to create a second
   *  copy of a listing that is already on file. */
  failed: boolean;
  onRetry: () => void;
  retrying: boolean;
  failureNoun: string;
  rows: T[];
  selected: T | null;
  onSelect: (row: T) => void;
  selectedLabel: (row: T) => string;
  selectedCaption: (row: T) => string;
  rowLabel: (row: T) => string;
  rowCaption: (row: T) => string;
  rowDisabled?: (row: T) => boolean;
  rowDisabledReason?: (row: T) => string;
  /** 🔴 Clears the CHOICE, not just the search box. This used to reset the
   *  search term alone, leaving the chosen card exactly where it was — so
   *  "Choose a different buyer" looked like the way back and did nothing. */
  onClear: () => void;
}) {
  return (
    <div className="space-y-space-2">
      <PickerLabel>{label}</PickerLabel>

      {selected ? (
        <SelectedCard
          testId={testId}
          label={selectedLabel(selected)}
          caption={selectedCaption(selected)}
          changeLabel={`Choose a different ${label.toLowerCase()}`}
          onChange={() => {
            onSearch("");
            onClear();
          }}
        />
      ) : (
        <>
          <Input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={placeholder}
            aria-label={placeholder}
            data-testid={`${testId}-search`}
          />
          {loading ? (
            <Skeleton className="h-32 w-full" />
          ) : failed ? (
            <InlineQueryError what={failureNoun} onRetry={onRetry} retrying={retrying} />
          ) : rows.length === 0 ? (
            <EmptyState
              title={`No ${label.toLowerCase()}s match`}
              description="Try a different search."
              variant="noResults"
              className="py-space-4"
            />
          ) : (
            <ul
              className="grid gap-space-1 list-none p-0 m-0 max-h-64 overflow-y-auto"
              data-testid={`${testId}-results`}
            >
              {rows.map((row) => {
                const disabled = rowDisabled?.(row) ?? false;
                return (
                  <li key={row.id}>
                    <RowButton
                      testId={`${testId}-row`}
                      label={rowLabel(row)}
                      caption={
                        disabled && rowDisabledReason
                          ? rowDisabledReason(row)
                          : rowCaption(row)
                      }
                      disabled={disabled}
                      onClick={() => onSelect(row)}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
